import { html, nothing } from 'lit';
import { classMap } from 'lit-html/directives/class-map.js';
import componentStyles from './location-app.css.lit.ts';
import QueryComponent from '../../base/query-component/QueryComponent.js';
import { ifDefined } from 'lit-html/directives/if-defined.js';

import {
  generateFilterLabel,
  filterCount,
  parseLocationItems,
} from './location-app-helpers';

export const MAP_VIEW = 'map_view';
export const LIST_VIEW = 'list_view';

export const TOGGLE_ITEMS = [
  { id: MAP_VIEW, label: Drupal.t('Map View'), icon: 'map' },
  { id: LIST_VIEW, label: Drupal.t('List View'), icon: 'list' },
];

export default class LocationApp extends QueryComponent {
  static styles = [super.styles, componentStyles];

  static get properties() {
    return {
      activeTab: { type: String },
      breadcrumbs: { type: Object },
      locationFilters: { type: Array },
      locationItems: { type: Array },
      mapType: { type: String },
      pageTitle: { type: String },
      staticImage: { type: String },
    };
  }

  constructor() {
    super();
    this.checkedFilters = [];
    this.locationFilters = [];
    this.toggle = TOGGLE_ITEMS;
    this.activeTab = LIST_VIEW;
    this._hasFilterScreen = false;
    this._hasInitialMapRender = true;
    this._activePoint = undefined; // { location: Object, id: String}
    this._footerIsVisible = false;
    this._showAllFilters = false;
  }

  firstUpdated() {
    this._displayedLocations = parseLocationItems(this.locationItems);
    const activeFilters = window.location.search
      .substr(1)
      .replace(/type\[\]=/g,'')
      .split('&')

    this.locationFilters = this.locationFilters.map(filter => {
      const item = filter;
      item.checked = activeFilters.includes(item.key);
      return item;
    });
    this._setFilterCount();
  }

  _onFilterButtonClick() {
    this._hasFilterScreen = !this._hasFilterScreen;
    this.requestUpdate();
  }

  _onViewMap = activePoint => {
    if (this._activePoint && this._activePoint.id === activePoint.id) {
      this._activePoint = undefined;
      this.update();
    }

    this._activePoint = activePoint;
    this._handleToggle({ id: MAP_VIEW });
  };

  _onFilterClick(event) {
    this.locationFilters.forEach(filter => {
      const item = filter;
      if (item.key === event.target.getAttribute('key')) {
        item.checked = event.target.shadowRoot.querySelector('input').checked;
      }
    });

    const checkedFilters = this.locationFilters
      .filter(filter => filter.checked)
      .map(filter => filter.key);

    this.checkedFilters = checkedFilters;

    let params = []
    const currentURL = window.location.href.split(/[?#]/)[0];
    checkedFilters.forEach(filter => params.push('type[]=' + filter))
    window.location.href = currentURL + '?' + params.join('&')

    this._setFilterCount();
    this.requestUpdate();
  }

  _handleToggle = item => {
    this.activeTab = item.id;

    /**
     * Addresses a sizing related issue with azure
     * If the map is mounted initial for whatever reason the map css will not fit the container css when the container expands
     */
    if (this.activeTab === MAP_VIEW && !this._hasInitialMapRender) {
      this._hasInitialMapRender = true;
    }
    // @todo: remove this quick and dirty workaround and redraw/update the map size properly
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 25);

    window.dispatchEvent(
      new CustomEvent('hf-footer-set-state', {
        detail: {
          hidden: this.activeTab === MAP_VIEW,
        },
      })
    );

    this.requestUpdate();
  };

  _setFilterCount() {
    this.locationFilters = filterCount(
      this.locationFilters,
      this._displayedLocations
    );
  }

  _renderFilterNodes() {
    const sliceQuantity = this._showAllFilters ? this.locationFilters.length : 5;
    return html` <form>
      ${this.locationFilters.slice(0,sliceQuantity).map(filter => {
        return html`
          <div class="location-app__filters-item">
            <hf-checkbox
              @click=${this._onFilterClick}
              label=${generateFilterLabel(filter.label, filter.count)}
              key=${filter.key}
              checked=${ifDefined(filter.checked ? filter.checked : undefined)}
            ></hf-checkbox>
            <hf-icon
              name="chevronRight"
              class="location-app__filters-arrow-icon"
              styleclass="location-filter-arrow"
            ></hf-icon>
          </div>
        `;
      })}
    </form>`;
  }

  setControlState(state) {
    this._footerIsVisible = state;
    this.requestUpdate();
  }

  toggleAllFilters() {
    this._showAllFilters = !this._showAllFilters;
    this.requestUpdate();
  }

  render() {
    return html`
      <div class="location-app" id="location-app">
        <div class="location-app__header">
          <hf-search-hero
            contenttype="location"
            searchUrl="/search?search="
            .breadCrumbLinks="${this.breadcrumbs.breadcrumbLinks}"
            breadcrumbTitle="${this.breadcrumbs.breadcrumbTitle}"
            header="${this.pageTitle}"
            placeHolder="${Drupal.t('Search by Name or Address')}"
            searchTextMobile="${Drupal.t('Search by Name or Address')}"
            buttonText="${Drupal.t('Search')}"
          >
            <div slot="breadcrumb-children">
              <slot name="breadcrumb-content"></slot>
            </div>
            <div slot="filters">${this._renderFilterNodes()}</div>
          </hf-search-hero>
        </div>

        <div class="location-app__content">
          <div
            class="${classMap({
              'location-app__filters': true,
              'location-app__filters--hidden': !this._hasFilterScreen,
            })}"
          >
            <button
              class="location-app__filters-close"
              @click=${this._onFilterButtonClick}
            >
              <hf-icon
                name="filter"
                class="location-app__filters-close--small-icon"
              ></hf-icon>
              ${Drupal.t('Filter')}
              <hf-icon
                name="close"
                class="location-app__filters-close--icon"
                styleclass="middle"
              ></hf-icon>
            </button>
            ${this._renderFilterNodes()}
            <button
              class="location-app__filters-see-all"
              @click=${this.toggleAllFilters}
            >
            ${this._showAllFilters
              ? html`${Drupal.t('See less')}`
              : html`${Drupal.t('See all')}` }
              <hf-icon
                name="arrow-right"
                class="splide__arrow--icon">
              </hf-icon>
            </button>
          </div>
          <div
            class="${classMap({
              'location-app__list': true,
              'location-app__list--hidden': this.activeTab === MAP_VIEW,
            })}"
          >
            <div class="location-app__list-heading">
              ${Array.isArray(this._displayedLocations) &&
              html` <hf-heading
                levelstyle="location-heading"
                class="location-list__card-count"
              >
                ${Drupal.t('Showing @length locations', {
                  '@length': this._displayedLocations.length,
                })}
              </hf-heading>`}
            </div>

            <hf-location-list
              .cards=${this._displayedLocations}
              .onViewMap=${this._onViewMap}
            ></hf-location-list>
          </div>
          <div
            class="${classMap({
              'location-app__fakecontrols': true,
              'location-app__fakecontrols--hidden': this._footerIsVisible,
            })}"
          ></div>
          <div
            class="${classMap({
              'location-app__controls': true,
              'location-app__controls--static': this._footerIsVisible,
            })}"
          >
            <button
              class="location-app__filters-close location-app__filters-close--bottom"
              @click=${this._onFilterButtonClick}
            >
              <hf-icon
                name="filter"
                class="location-app__filters-close--small-icon"
              ></hf-icon>
              ${Drupal.t('Filter')}
            </button>
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define('hf-location-app', LocationApp);
